import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { AssistantSkill, useAssistantSkillsLazyQuery } from '../../../generated/graphql'
import { ChatMessage } from '../chatMessage'
import { AssistantChatInputComboboxProps, AssistantChatInputValue } from '../AssistantChatInput'
import { SKILL_SLASH_MENU_LISTBOX_ID, skillSlashMenuOptionId } from '../SkillSlashMenu'
import rankSkills from '../SkillSlashMenu/rankSkills'
import detectSlashCommand, { SlashCommandMatch } from './detectSlashCommand'

export interface UseSkillSlashArgs {
  inputValue: AssistantChatInputValue
  setInputValue: (next: AssistantChatInputValue) => void
  sendMessage: (text: string, skill?: string) => void
  messages: ChatMessage[]
  inputRef: RefObject<HTMLDivElement>
}

export interface UseSkillSlashResult {
  slash: SlashCommandMatch | null
  menuOpen: boolean
  filtered: AssistantSkill[]
  activeIndex: number
  setActiveIndex: Dispatch<SetStateAction<number>>
  skillsLoading: boolean
  skillsError: boolean
  comboboxProps: AssistantChatInputComboboxProps
  handleInputChange: (next: AssistantChatInputValue) => void
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => boolean
  selectSkill: (skill: AssistantSkill) => void
  closeMenu: () => void
  /**
   * Clears the unknown-skill auto-resend fingerprint so a fresh user send
   * with the same (text, skill) pair re-arms the auto-fallback. Call this
   * from the component's send handler.
   */
  resetUnknownSkillGuard: () => void
}

/**
 * Read the current caret position inside a `contentEditable` root, expressed
 * as a character offset that ignores the (non-editable) skill chip.
 *
 * Returns the length of `text` when the selection is missing, collapsed
 * outside the root, or otherwise unparseable — matching the "caret at end"
 * convention the slash detector expects after a state-driven text rewrite.
 */
const getCaretOffset = (root: HTMLElement | null, text: string): number => {
  if (!root) return text.length
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return text.length
  const range = selection.getRangeAt(0)
  if (!root.contains(range.endContainer)) return text.length
  // Pre-range walks from the start of `root` to the caret; its toString()
  // skips no text but does include the chip's textContent. We strip the chip
  // contribution by subtracting its length when the chip is in the pre-range.
  const pre = range.cloneRange()
  pre.selectNodeContents(root)
  pre.setEnd(range.endContainer, range.endOffset)
  const { length: rawLength } = pre.toString()
  const chip = root.querySelector('[data-skill-chip]') as HTMLElement | null
  const chipLength = chip && pre.intersectsNode(chip) ? chip.textContent?.length ?? 0 : 0
  return Math.max(0, rawLength - chipLength)
}

const isUnknownSkillError = (text: string): boolean =>
  /unknown skill|skill .* (is not|no longer)|invalid skill/i.test(text)

/**
 * Owns the slash-command state machine for the assistant chat input:
 * caret tracking, slash-token detection, ranked filtering, active-index
 * navigation, skill-chip insertion, combobox ARIA wiring, and unknown-skill
 * auto-resend recovery. Keeps these concerns out of `AssistantChat`, which
 * is then free to focus on layout, message rendering, and send/stop.
 *
 * `handleKeyDown` returns `true` when the hook consumed the event (so the
 * caller can short-circuit). Otherwise the caller routes the key normally
 * (Enter-to-send, Escape-to-stop while loading, etc.).
 */
const useSkillSlash = ({
  inputValue,
  setInputValue,
  sendMessage,
  messages,
  inputRef
}: UseSkillSlashArgs): UseSkillSlashResult => {
  const [caret, setCaret] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const skillsLoadedRef = useRef(false)
  const lastUnknownSkillSendRef = useRef<string | null>(null)
  const textRef = useRef(inputValue.text)
  textRef.current = inputValue.text

  const [loadSkills, { data, loading: skillsLoading, error: skillsError }] =
    useAssistantSkillsLazyQuery({ fetchPolicy: 'cache-first' })
  const skills = useMemo<AssistantSkill[]>(() => data?.assistantSkills ?? [], [data])

  // Recompute the slash match from the *current* caret, but only when a chip
  // is absent (req 4 — typing `/` after a chip stays literal).
  const slash: SlashCommandMatch | null = useMemo(
    () => (inputValue.skill === null ? detectSlashCommand(inputValue.text, caret) : null),
    [inputValue.text, inputValue.skill, caret]
  )
  const menuOpen = slash !== null
  const slashQuery = slash?.query ?? ''
  const filtered = useMemo(
    () => (menuOpen ? rankSkills(skills, slashQuery) : []),
    [menuOpen, skills, slashQuery]
  )

  // Fire the lazy query the first time the menu opens; subsequent opens
  // re-use Apollo's cache (cache-first).
  useEffect(() => {
    if (menuOpen && !skillsLoadedRef.current) {
      skillsLoadedRef.current = true
      loadSkills()
    }
  }, [menuOpen, loadSkills])

  // Reset highlight when the slash query changes.
  useEffect(() => {
    setActiveIndex(0)
  }, [slash?.query])

  // Surface unknown-skill rejections by clearing the chip and resending the
  // same text without a skill (req 20). We track the last (text, skill) we
  // tried so a transient retry does not loop on a stable error.
  useEffect(() => {
    const last = messages[messages.length - 1]
    if (!last || last.sender !== 'assistant' || !isUnknownSkillError(last.text)) return
    const prior = messages.length >= 2 ? messages[messages.length - 2] : null
    if (!prior || prior.sender !== 'user' || !prior.skill) return
    const fingerprint = `${prior.text}::${prior.skill}`
    if (lastUnknownSkillSendRef.current === fingerprint) return
    lastUnknownSkillSendRef.current = fingerprint
    sendMessage(prior.text)
  }, [messages, sendMessage])

  /**
   * Replace the slash query with empty text and stamp the picked skill.
   * Refocuses the editable area at end-of-text + a trailing space so the
   * advisor can immediately start typing parameters.
   */
  const selectSkill = useCallback(
    (skill: AssistantSkill) => {
      if (slash === null) return
      const before = inputValue.text.slice(0, slash.start)
      const after = inputValue.text.slice(slash.end)
      const cleaned = `${before}${after}`
      // Always end the post-chip text with a space so the advisor can start
      // typing parameters immediately. For an empty input this produces a
      // single leading space; for a partial input it ensures a separator.
      const nextText = cleaned.endsWith(' ') ? cleaned : `${cleaned} `
      setInputValue({ text: nextText, skill: skill.name })
      setCaret(nextText.length)
      // Defer focus until the input has rendered the new text.
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    },
    [inputValue.text, slash, setInputValue, inputRef]
  )

  const closeMenu = useCallback(() => {
    if (!slash) return
    const cleaned = inputValue.text.slice(0, slash.start) + inputValue.text.slice(slash.end)
    setInputValue({ text: cleaned, skill: inputValue.skill })
    setCaret(slash.start)
  }, [inputValue.skill, inputValue.text, slash, setInputValue])

  const handleInputChange = useCallback(
    (next: AssistantChatInputValue) => {
      setInputValue(next)
      // The DOM was just mutated; sample the live selection so the slash
      // detector reads the post-edit caret position.
      setCaret(getCaretOffset(inputRef.current, next.text))
    },
    [setInputValue, inputRef]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): boolean => {
      if (!menuOpen) return false
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(filtered.length - 1, Math.max(0, i + 1)))
        return true
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(0, i - 1))
        return true
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && filtered.length > 0) {
        e.preventDefault()
        const target = filtered[Math.min(activeIndex, filtered.length - 1)]
        selectSkill(target)
        return true
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        // The wrapping MUI Drawer also listens for Esc and would close itself.
        // Stop propagation so the drawer stays open when the user is just
        // dismissing the slash menu.
        e.stopPropagation()
        closeMenu()
        return true
      }
      return false
    },
    [activeIndex, closeMenu, filtered, menuOpen, selectSkill]
  )

  // Track the caret on click / arrow-key navigation so the slash detector
  // sees the latest position. The handler reads from `textRef` (kept in sync
  // every render) so this listener is mounted once, not torn down per
  // keystroke.
  useEffect(() => {
    const handler = () => {
      const root = inputRef.current
      const selection = window.getSelection()
      if (!root || !selection || selection.rangeCount === 0) return
      // Ignore selections outside the editable root. Without this gate,
      // selecting text elsewhere on the page snaps the tracked caret to
      // end-of-text (getCaretOffset's fallback) and dismisses an open menu.
      if (!root.contains(selection.getRangeAt(0).endContainer)) return
      setCaret(getCaretOffset(root, textRef.current))
    }
    document.addEventListener('selectionchange', handler)
    return () => document.removeEventListener('selectionchange', handler)
  }, [inputRef])

  // Combobox ARIA: textbox links to the listbox via aria-controls and points
  // at the active option via aria-activedescendant. Without these, the slash
  // menu is invisible to screen readers (WCAG 2.1 AA, 1.3.1 + 4.1.2).
  const comboboxProps = useMemo<AssistantChatInputComboboxProps>(
    () => ({
      'aria-haspopup': 'listbox',
      'aria-autocomplete': 'list',
      'aria-expanded': menuOpen,
      ...(menuOpen && {
        'aria-controls': SKILL_SLASH_MENU_LISTBOX_ID,
        ...(filtered.length > 0 && {
          'aria-activedescendant': skillSlashMenuOptionId(
            Math.min(activeIndex, filtered.length - 1)
          )
        })
      })
    }),
    [menuOpen, filtered.length, activeIndex]
  )

  const resetUnknownSkillGuard = useCallback(() => {
    lastUnknownSkillSendRef.current = null
  }, [])

  return {
    slash,
    menuOpen,
    filtered,
    activeIndex,
    setActiveIndex,
    skillsLoading,
    skillsError: !!skillsError,
    comboboxProps,
    handleInputChange,
    handleKeyDown,
    selectSkill,
    closeMenu,
    resetUnknownSkillGuard
  }
}

export default useSkillSlash
